import React, { useEffect, useState } from "react";
import { Loader2, Plus, Pencil, Trash2 } from "lucide-react";
import { AdminLayout } from "./AdminLayout";
import { api, euro, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";

const EMPTY = {
  name_nl: "", name_fr: "", category: "herstelling", description_nl: "", description_fr: "",
  base_price: 0, vat_rate: 21, included_time: 60, extra_time_price: 45, partner_fee: 0, icon: "wrench", active: true,
};

export default function AdminServices() {
  const [services, setServices] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/services", { params: { include_inactive: true } }).then((r) => setServices(r.data)).catch(() => setServices([]));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm(s); setOpen(true); };

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form, base_price: Number(form.base_price), partner_fee: Number(form.partner_fee), vat_rate: Number(form.vat_rate), included_time: Number(form.included_time), extra_time_price: Number(form.extra_time_price) };
      if (editing) await api.put(`/services/${editing.id}`, payload);
      else await api.post("/services", payload);
      toast.success("Dienst opgeslagen");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const del = async (s) => { if (window.confirm("Dienst verwijderen?")) { await api.delete(`/services/${s.id}`); load(); } };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900">Diensten & prijzen</h1>
        <Button className="cta-orange rounded-full" onClick={openNew} data-testid="add-service-btn"><Plus size={16} className="mr-1" /> Dienst toevoegen</Button>
      </div>

      {services === null ? <div className="flex justify-center py-20"><Loader2 className="animate-spin text-slate-400" /></div> : (
        <div className="mt-6 bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-3">Dienst</th>
                  <th className="text-right px-4 py-3">Basisprijs</th>
                  <th className="text-right px-4 py-3 hidden sm:table-cell">Btw</th>
                  <th className="text-right px-4 py-3">Partnervergoeding</th>
                  <th className="text-right px-4 py-3 hidden md:table-cell">Marge</th>
                  <th className="text-center px-4 py-3">Actief</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {services.map((s) => (
                  <tr key={s.id} data-testid={`service-row-${s.id}`}>
                    <td className="px-4 py-3 font-medium text-slate-900">{s.name_nl}<span className="block text-xs text-slate-400">{s.name_fr}</span></td>
                    <td className="px-4 py-3 text-right">{euro(s.base_price)}</td>
                    <td className="px-4 py-3 text-right hidden sm:table-cell">{s.vat_rate}%</td>
                    <td className="px-4 py-3 text-right text-orange-700">{euro(s.partner_fee)}</td>
                    <td className="px-4 py-3 text-right hidden md:table-cell text-green-700">{euro(s.base_price - s.partner_fee)}</td>
                    <td className="px-4 py-3 text-center"><span className={`inline-block h-2.5 w-2.5 rounded-full ${s.active ? "bg-green-500" : "bg-slate-300"}`} /></td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(s)} data-testid={`edit-service-${s.id}`}><Pencil size={14} /></Button>
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => del(s)} data-testid={`delete-service-${s.id}`}><Trash2 size={14} /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="service-dialog">
          <DialogHeader><DialogTitle>{editing ? "Dienst bewerken" : "Nieuwe dienst"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Naam (NL)</Label><Input className="mt-1" value={form.name_nl} onChange={(e) => setForm({ ...form, name_nl: e.target.value })} data-testid="service-name-nl" /></div>
              <div><Label>Naam (FR)</Label><Input className="mt-1" value={form.name_fr} onChange={(e) => setForm({ ...form, name_fr: e.target.value })} data-testid="service-name-fr" /></div>
            </div>
            <div><Label>Beschrijving (NL)</Label><Textarea className="mt-1" value={form.description_nl} onChange={(e) => setForm({ ...form, description_nl: e.target.value })} data-testid="service-desc-nl" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Basisprijs (€)</Label><Input type="number" className="mt-1" value={form.base_price} onChange={(e) => setForm({ ...form, base_price: e.target.value })} data-testid="service-base-price" /></div>
              <div><Label>Partnervergoeding (€)</Label><Input type="number" className="mt-1" value={form.partner_fee} onChange={(e) => setForm({ ...form, partner_fee: e.target.value })} data-testid="service-partner-fee" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Btw %</Label><Input type="number" className="mt-1" value={form.vat_rate} onChange={(e) => setForm({ ...form, vat_rate: e.target.value })} data-testid="service-vat" /></div>
              <div><Label>Inbegr. tijd</Label><Input type="number" className="mt-1" value={form.included_time} onChange={(e) => setForm({ ...form, included_time: e.target.value })} data-testid="service-time" /></div>
              <div><Label>Extra tijd €</Label><Input type="number" className="mt-1" value={form.extra_time_price} onChange={(e) => setForm({ ...form, extra_time_price: e.target.value })} data-testid="service-extra" /></div>
            </div>
            <label className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="service-active" /> Actief</label>
            <Button className="cta-orange rounded-full w-full" disabled={busy || !form.name_nl} onClick={save} data-testid="save-service-btn">{busy ? <Loader2 className="animate-spin" /> : "Opslaan"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
